import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACTIONS, EVENTS, Joyride, STATUS } from "react-joyride";

import TourTooltip from "./TourTooltip";

const FeatureTourGuide = ({
  run,
  steps,
  onComplete,
  showMeIndices,
  showMeCallback,
  locale = {},
}) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);

  const showMeStartIndex = Number.isInteger(showMeIndices?.start)
    ? showMeIndices.start
    : null;
  const showMeEndIndex = Number.isInteger(showMeIndices?.end)
    ? showMeIndices.end
    : null;
  const isShowMeStep =
    Number.isInteger(showMeStartIndex) && stepIndex === showMeStartIndex;
  const nextButtonLabel = isShowMeStep
    ? t("featureTour.showMe")
    : t("featureTour.next");

  const resolvedLocale = {
    back: t("featureTour.prev"),
    next: isShowMeStep ? t("featureTour.showMe") : t("featureTour.next"),
    close: t("ValueStrings.close"),
    last: t("featureTour.gotIt"),
    ...locale,
  };

  useEffect(() => {
    if (run) {
      setStepIndex(0);
    }
  }, [run]);

  const handleCallback = useCallback(
    (tourState) => {
      const { action, index, status, type } = tourState;

      // Call onComplete callback when the tour finishes or is skipped
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        onComplete?.(tourState);
      }

      const isStepAfter = type === EVENTS.STEP_AFTER;
      const shouldAdvance =
        isStepAfter ||
        (type === EVENTS.TARGET_NOT_FOUND && status === STATUS.RUNNING);
      // Don't advance the step index if we're waiting for user to proceed
      if (!shouldAdvance) {
        return;
      }

      // Handle "Show Me" prev logic
      if (
        isStepAfter &&
        action === ACTIONS.PREV &&
        Number.isInteger(showMeStartIndex) &&
        index === showMeStartIndex + 1
      ) {
        showMeCallback?.(false);
      }

      // Handle "Show Me" next logic
      if (
        isStepAfter &&
        action === ACTIONS.NEXT &&
        index === showMeStartIndex
      ) {
        showMeCallback?.(true);
      }

      const nextStepIndex = Math.max(
        index + (action === ACTIONS.PREV ? -1 : 1),
        0,
      );

      // Handle "Show Me" end logic
      if (
        isStepAfter &&
        action === ACTIONS.NEXT &&
        Number.isInteger(showMeEndIndex) &&
        index === showMeEndIndex
      ) {
        showMeCallback?.(false);

        // Add a small delay before advancing from the "Show Me" step to ensure the callback has time to update the UI
        setTimeout(() => {
          setStepIndex(nextStepIndex);
        }, 500);
        return;
      }

      setStepIndex(nextStepIndex);
    },
    [onComplete, showMeCallback, showMeStartIndex, showMeEndIndex],
  );

  const renderTooltip = useCallback(
    (tooltipProps) => (
      <TourTooltip {...tooltipProps} nextLabel={nextButtonLabel} />
    ),
    [nextButtonLabel],
  );

  if (!Array.isArray(steps) || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      continuous
      run={run}
      steps={steps}
      tooltipComponent={renderTooltip}
      onEvent={handleCallback}
      stepIndex={stepIndex}
      locale={resolvedLocale}
      options={{
        overlayClickAction: false,
        // skipScroll: true,
        dismissKeyAction: false,
        skipBeacon: true,
        blockTargetInteraction: true,
        arrowBase: 16,
        arrowSize: 8,
        arrowSpacing: 8,
        arrowColor: "rgba(0, 0, 0, 0.75)",
      }}
    />
  );
};

export default FeatureTourGuide;
